import React, { useId } from "react"
import Pallino from "./Pallino"

// Le illustrazioni del sito. Fondo acceso, una trama di righe chiare,
// ombre piatte a terra: lo stile delle animazioni a cui si ispira il sito
// (docs/sito.md). Di giorno c'è il sole, la sera la luna, le stelle e i
// riflettori accesi: le due versioni sono disegnate tutte e due, e il CSS
// (landing.css, `.is-day` e `.is-night`) mostra quella del tema.

const STARS = [
	[18, 22, 1.4, 0.9],
	[64, 58, 1, 0.6],
	[104, 16, 1.6, 0.9],
	[150, 80, 1.1, 0.5],
	[196, 30, 1.3, 0.8],
	[246, 96, 1, 0.5],
	[262, 14, 1.5, 0.9],
	[356, 118, 1.2, 0.6],
	[40, 140, 1, 0.4],
	[128, 128, 1.2, 0.5],
	[214, 150, 0.9, 0.4],
	[300, 150, 1.1, 0.5],
]

const Stars = () => (
	<g className="is-night">
		{STARS.map(([x, y, r, o]) => (
			<circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill="var(--c-white)" fillOpacity={o} />
		))}
	</g>
)

// Le chiome degli alberi dietro il telo, e le maglie della rete: tanti
// cerchi e tante righe, scritti una volta sola come un unico tracciato.
const dot = (x, y, r) => `M${x - r} ${y} a${r} ${r} 0 1 0 ${2 * r} 0 a${r} ${r} 0 1 0 ${-2 * r} 0`
const TREES = Array.from({ length: 15 }, (_, i) => dot(6 + i * 26, 84, 14 + (i % 3) * 3)).join(" ")
const NET_MESH = [
	...Array.from({ length: 30 }, (_, i) => `M${70 + i * 8} 160 V190`),
	"M60 175 H314 M60 182 H314",
].join(" ")
const NET = "M64 166 Q187 171 310 166 L310 188 L64 188 Z"

// L'apertura: un campo da tennis visto da dietro la linea di fondo. Pallino
// è in primo piano, col cestone delle palline; i cugini degli altri sport
// scelti stanno dall'altra parte della rete, e una pallina sta arrivando.
// Il campo è in prospettiva "piatta": le righe convergono, ma senza
// sfumature, come il resto delle illustrazioni.
export function HeroScene({ others = [] }) {
	const [left, right] = others
	const netClip = `net-${useId().replace(/:/g, "")}`
	return (
		<div className="scene scene-hero">
			<svg viewBox="0 0 374 300" aria-hidden="true" className="scene-svg">
				<defs>
					<clipPath id={netClip}>
						<path d={NET} />
					</clipPath>
				</defs>
				<rect width="374" height="300" fill="var(--c-court-sky)" />
				<g className="is-day">
					<circle cx="330" cy="40" r="28" fill="var(--c-white)" fillOpacity="0.28" />
					<circle cx="330" cy="40" r="18" fill="var(--c-scene-sun)" />
					<path d="M28 44 q11 -10 23 0 q10 -8 20 0 z" fill="var(--c-scene-cloud)" />
					<path d="M226 28 q12 -10 24 0 q10 -8 20 0 z" fill="var(--c-scene-cloud)" />
				</g>
				<Stars />
				<g className="is-night">
					<circle cx="288" cy="42" r="15" fill="var(--c-scene-moon)" />
					<circle cx="295" cy="36" r="13" fill="var(--c-court-sky)" />
				</g>

				{/* gli alberi e il telo verde dietro il campo */}
				<path d={TREES} fill="var(--c-court-trees)" />
				<rect y="84" width="374" height="40" fill="var(--c-court-fence)" />
				<path
					d="M0 84 H374 M46 84 V124 M92 84 V124 M138 84 V124 M184 84 V124 M230 84 V124 M276 84 V124 M322 84 V124 M368 84 V124"
					stroke="var(--c-court-fence-post)"
					strokeWidth="2"
					fill="none"
				/>
				<g className="is-night">
					<rect x="30" y="24" width="4" height="100" fill="var(--c-scene-pole)" />
					<rect x="20" y="18" width="24" height="8" rx="3" fill="var(--c-scene-lamp)" />
					<rect x="340" y="24" width="4" height="100" fill="var(--c-scene-pole)" />
					<rect x="330" y="18" width="24" height="8" rx="3" fill="var(--c-scene-lamp)" />
				</g>

				{/* il campo: il bordo, il cemento e le righe */}
				<rect y="122" width="374" height="178" fill="var(--c-court-outer)" />
				<path d="M110 128 L264 128 L368 292 L6 292 Z" fill="var(--c-court-surface)" />
				<path
					d="M110 128 L264 128 L368 292 L6 292 Z M129.3 128 L51.3 292 M244.7 128 L322.7 292 M115 158 H259 M76 240 H298 M187 158 V240 M187 128 V133 M187 292 V286"
					stroke="var(--c-court-line)"
					strokeWidth="2.2"
					fill="none"
				/>

				{/* la pallina che arriva dall'altra parte */}
				<circle cx="192" cy="138" r="4.5" fill="var(--c-scene-ball)" />
				<path
					d="M196 132 L206 118 M190 131 L196 121"
					stroke="var(--c-white)"
					strokeOpacity="0.7"
					strokeWidth="1.5"
					strokeLinecap="round"
				/>

				{/* la rete */}
				<path d="M64 188 L310 188 L320 195 L54 195 Z" fill="var(--c-scene-shade)" fillOpacity="0.2" />
				<path d={NET} fill="var(--c-court-net)" />
				<path
					d={NET_MESH}
					clipPath={`url(#${netClip})`}
					stroke="var(--c-court-mesh)"
					strokeWidth="0.8"
					fill="none"
				/>
				<path d="M64 166 Q187 171 310 166" stroke="var(--c-court-tape)" strokeWidth="3" fill="none" />
				<path d="M187 170 V188" stroke="var(--c-court-tape)" strokeWidth="2.5" />
				<rect x="61" y="162" width="5" height="28" rx="1.5" fill="var(--c-court-post)" />
				<rect x="308" y="162" width="5" height="28" rx="1.5" fill="var(--c-court-post)" />

				{/* palline sparse, e il cestone in primo piano */}
				<circle cx="276" cy="214" r="4" fill="var(--c-scene-ball)" />
				<circle cx="122" cy="210" r="4" fill="var(--c-scene-ball-shade)" />
				<circle cx="300" cy="270" r="5.5" fill="var(--c-scene-ball)" />
				<ellipse cx="66" cy="291" rx="40" ry="5" fill="var(--c-scene-shade)" fillOpacity="0.35" />
				<path
					d="M38 288 V293 M90 288 V293"
					stroke="var(--c-scene-basket-wire)"
					strokeWidth="3"
					strokeLinecap="round"
				/>
				<path d="M34 240 L98 240 L93 288 L39 288 Z" fill="var(--c-scene-basket)" />
				<path
					d="M36 256 H96 M37.5 272 H94.5 M55 240 V288 M77 240 V288"
					stroke="var(--c-scene-basket-wire)"
					strokeWidth="2.4"
				/>
				<rect x="30" y="234" width="72" height="8" rx="4" fill="var(--c-scene-basket-wire)" />
				<circle cx="44" cy="231" r="7" fill="var(--c-scene-ball)" />
				<circle cx="59" cy="229" r="7" fill="var(--c-scene-ball-shade)" />
				<circle cx="74" cy="229" r="7" fill="var(--c-scene-ball)" />
				<circle cx="89" cy="231" r="7" fill="var(--c-scene-ball-shade)" />

				<g className="is-night">
					<path
						d="M32 26 L150 300 L-40 300 Z M342 26 L414 300 L224 300 Z"
						fill="var(--c-scene-lamp)"
						fillOpacity="0.09"
					/>
				</g>
			</svg>

			{left && <Pallino className="scene-left" kind={left} pose="felice" />}
			{right && <Pallino className="scene-right" kind={right} />}
			{/* il posto di Pallino: lui è uno solo, e da qui parte per
			    accompagnare chi scorre (PallinoGuide) */}
			<div className="scene-lead" id="pallino-casa" />
		</div>
	)
}

// La chiusura: lo stesso cielo. Il posto in mezzo è di Pallino, che ci
// atterra a fine pagina (PallinoGuide): di giorno salta, la sera dorme.
export function ClosingSky({ children }) {
	return (
		<div className="scene scene-closing">
			<svg
				viewBox="0 0 374 200"
				preserveAspectRatio="xMidYMin slice"
				aria-hidden="true"
				className="scene-svg">
				<g fill="none" stroke="var(--c-scene-lines)" strokeWidth="4">
					<path d="M-10 30 H80 V80 H20" />
					<path d="M300 -10 V40 H384" />
				</g>
				<Stars />
			</svg>
			<div className="scene-closing-pallino">
				<div className="scene-closing-slot" id="pallino-fine" />
			</div>
			{children}
		</div>
	)
}

// Il quaderno a matita, con le frecce di chi ha spostato le lezioni
export function Notebook() {
	return (
		<svg viewBox="0 0 120 100" width="120" height="100" aria-hidden="true" className="notebook">
			<g transform="rotate(12 60 50)">
				<rect x="24" y="10" width="70" height="86" rx="6" fill="var(--c-surface)" />
				<rect x="24" y="10" width="10" height="86" rx="4" fill="var(--c-primary)" />
				<path
					d="M42 28 h40 M42 40 h34 M42 52 h40 M42 64 h26"
					stroke="var(--c-gunmetal-light)"
					strokeOpacity="0.6"
					strokeWidth="3"
					strokeLinecap="round"
				/>
				<g stroke="var(--c-red)" strokeWidth="2.4" fill="none" strokeLinecap="round">
					<path d="M50 34 q14 10 26 2 m-4 -6 l4 6 l-7 2" />
					<path d="M46 58 q10 14 30 8" />
				</g>
			</g>
		</svg>
	)
}
